import * as React from "react";
import { Link, useParams } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { AlertCircle, CheckCircle2, CircleDashed, ExternalLink, GitPullRequest, Loader2, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { usePageTitle } from "@/hooks/use-page-title";
import { useMarkPullRequestReady, usePullRequest } from "@/hooks/queries/use-pull-requests";
import { Button } from "@/components/ui/button";
import { useJob } from "@/hooks/queries/use-mailbox";
import { useProjectDeployments } from "@/hooks/queries/use-cloud";
import { ApiError } from "@/lib/api/client";
import type { PullRequestDeployment, PullRequestView } from "@/lib/api/pull-requests";
import { AgentReviewCard } from "@/components/pull-requests/agent-review-card";
import { PullRequestFiles } from "@/components/pull-requests/pull-request-files";
import { DeployRecordSummary } from "@/components/pull-requests/deploy-record-summary";
import {
  DeployDialog,
  MergeDialog,
  ResolveConflictsDialog,
  SendBackDialog,
} from "@/components/pull-requests/pull-request-dialogs";
import {
  describeChecks,
  describeMergeability,
  hasConflicts,
  shortSha,
} from "@/components/pull-requests/pull-request-status";
import { useIdeaHref } from "@/hooks/use-new-look";

const STATE_BADGE: Record<PullRequestView["state"], { label: string; className: string }> = {
  open: { label: "Open", className: "bg-green-600 text-white border-transparent" },
  merged: { label: "Merged", className: "bg-purple-600 text-white border-transparent" },
  closed: { label: "Closed", className: "bg-muted text-muted-foreground border-transparent" },
};

const JOB_STATUS_LABELS: Record<string, string> = {
  pending: "Waiting to start",
  claimed: "Starting",
  running: "Working",
  awaiting_input: "Waiting for you",
  waiting: "Waiting",
  cancelling: "Cancelling",
  done: "Finished",
  failed: "Failed",
  cancelled: "Cancelled",
};

// "cancelling" counts as active: the runner hasn't stopped yet (ENG-302).
// "waiting" counts too (ENG-333): a fix job parked after a usage limit is
// still the agent's work on this PR and resumes on its own; the server would
// refuse a second fix run anyway.
const ACTIVE_JOB_STATUSES = new Set(["pending", "claimed", "running", "awaiting_input", "cancelling", "waiting"]);

const DEPLOY_STATE_LABELS: Record<string, string> = {
  none: "Not deployed",
  queued: "Waiting to start",
  in_progress: "Deploying",
  success: "Live",
  failure: "Failed",
  cancelled: "Cancelled",
};

function ChecksIcon({ tone }: { tone: ReturnType<typeof describeChecks>["tone"] }) {
  if (tone === "good") return <CheckCircle2 className="h-4 w-4 text-green-600" />;
  if (tone === "bad") return <XCircle className="h-4 w-4 text-red-600" />;
  if (tone === "waiting") return <CircleDashed className="h-4 w-4 text-amber-600 animate-spin" />;
  return <CircleDashed className="h-4 w-4 text-muted-foreground" />;
}

function FixJobRow({ jobId }: { jobId: string }) {
  const { data: job } = useJob(jobId);
  return (
    <li className="flex items-center gap-2 text-sm">
      <Link to={`/mailbox/jobs/${jobId}`} className="underline underline-offset-2">
        Agent run
      </Link>
      <span className="text-muted-foreground">
        {job ? (JOB_STATUS_LABELS[job.status] ?? job.status) : "Loading…"}
      </span>
      {job?.createdAt && (
        <span className="text-xs text-muted-foreground">
          {formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })}
        </span>
      )}
    </li>
  );
}

function DeploymentsList({ pr }: { pr: PullRequestView }) {
  const teamId = pr.projectId ?? "";
  const { data: current } = useProjectDeployments(teamId, !!pr.projectId && pr.deployments.length > 0, false);
  const statusFor = (d: PullRequestDeployment) => {
    const env = (current ?? []).find((c) => c.environment === d.environment);
    // The environment has moved on to another commit: this deploy was superseded.
    if (!env?.commit || !d.sha.startsWith(env.commit.slice(0, 7))) return null;
    return env;
  };
  if (pr.deployments.length === 0) return null;
  return (
    <section className="rounded-lg border p-4 space-y-2">
      <h2 className="text-sm font-semibold">Deploys</h2>
      <ul className="space-y-1">
        {[...pr.deployments].reverse().map((d) => {
          const env = statusFor(d);
          return (
            <li key={d.id} className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium capitalize">{d.environment}</span>
              <span className="font-mono text-xs text-muted-foreground">{shortSha(d.sha)}</span>
              <span className="text-muted-foreground">
                {env ? (DEPLOY_STATE_LABELS[env.state] ?? env.state) : "Started"}
              </span>
              <span className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(d.requestedAt), { addSuffix: true })}
              </span>
              {env?.state === "success" && env.url && (
                <a href={env.url} target="_blank" rel="noreferrer" className="text-xs underline">
                  Open app
                </a>
              )}
              {env?.runUrl && (
                <a href={env.runUrl} target="_blank" rel="noreferrer" className="text-xs underline">
                  Details
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** ER-130: a draft can't be merged; mark it ready here instead of on GitHub. */
function MarkReadyButton({ id }: { id: string }) {
  const markReady = useMarkPullRequestReady();
  return (
    <div className="space-y-1">
      <Button
        variant="outline"
        disabled={markReady.isPending}
        onClick={() => {
          markReady.mutate(id);
        }}
      >
        {markReady.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
        Mark ready for review
      </Button>
      {markReady.error && (
        <p className="text-xs text-red-600 dark:text-red-400">
          {markReady.error instanceof ApiError ? markReady.error.message : "Couldn't mark it ready."}
        </p>
      )}
    </div>
  );
}

export function PullRequestReviewPage() {
  const ideaHref = useIdeaHref();
  const { id } = useParams<{ id: string }>();
  const [page, setPage] = React.useState(1);
  const { data: pr, isLoading, error } = usePullRequest(id, page);
  usePageTitle(pr ? `PR #${String(pr.number)}` : "Pull request");

  const lastFixJobId = pr?.fixJobIds.at(-1);
  const { data: lastFixJob } = useJob(lastFixJobId);
  const fixJobActive = !!lastFixJob && ACTIVE_JOB_STATUSES.has(lastFixJob.status);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading pull request…
      </div>
    );
  }

  if (error || !pr) {
    const status = error instanceof ApiError ? error.response.status : null;
    return (
      <div className="p-6 space-y-2" role="alert">
        <div className="flex items-center gap-2 text-sm font-medium">
          <AlertCircle className="h-4 w-4 text-destructive" />
          {status === 404
            ? "This pull request couldn't be found."
            : status === 403
              ? "You don't have access to this pull request."
              : "This pull request couldn't be loaded."}
        </div>
        {error instanceof Error && status !== 404 && status !== 403 && (
          <p className="text-sm text-muted-foreground">{error.message}</p>
        )}
      </div>
    );
  }

  const checks = describeChecks(pr.checks);
  const conflicts = hasConflicts(pr);
  const fixDisabledReason =
    pr.state !== "open"
      ? "Only open pull requests can be sent back."
      : fixJobActive
        ? "The agent is already working on this pull request."
        : null;

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4 md:p-6">
      <header className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <GitPullRequest className="h-5 w-5" />
          <h1 className="text-xl font-semibold">{pr.title}</h1>
          <span className="text-muted-foreground">#{pr.number}</span>
          <Badge className={STATE_BADGE[pr.state].className}>{STATE_BADGE[pr.state].label}</Badge>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
          <span>{pr.repoFullName}</span>
          <span>
            <code>{pr.headRef}</code> → <code>{pr.baseRef}</code>
          </span>
          <a
            href={pr.htmlUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 hover:text-foreground"
          >
            Open on GitHub <ExternalLink className="h-3 w-3" />
          </a>
          {pr.jobId && (
            <Link to={`/mailbox/jobs/${pr.jobId}`} className="underline underline-offset-2">
              Agent run
            </Link>
          )}
          {pr.epicRequestId && (
            <Link to={ideaHref(pr.epicRequestId)} className="underline underline-offset-2">
              Epic request
            </Link>
          )}
        </div>
      </header>

      <section className="rounded-lg border p-4 space-y-2" aria-label="Status">
        <div className="flex items-center gap-2 text-sm">
          <ChecksIcon tone={checks.tone} />
          <span>{checks.text}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          {conflicts ? (
            <XCircle className="h-4 w-4 text-red-600" />
          ) : pr.state === "merged" || pr.mergeableState === "clean" ? (
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          ) : (
            <CircleDashed className="h-4 w-4 text-muted-foreground" />
          )}
          <span>{describeMergeability(pr)}</span>
        </div>
        {pr.state === "merged" && pr.mergedAt && (
          <p className="text-xs text-muted-foreground">
            Merged {formatDistanceToNow(new Date(pr.mergedAt), { addSuffix: true })} as {shortSha(pr.mergedSha)}
          </p>
        )}
        <DeployRecordSummary records={pr.deployRecords} />
      </section>

      <section className="flex flex-wrap items-start gap-2" aria-label="Actions">
        {pr.state === "open" && (pr.draft === true || pr.mergeableState === "draft") && <MarkReadyButton id={pr.id} />}
        {pr.state === "open" && <MergeDialog pr={pr} />}
        {pr.state === "open" && <SendBackDialog pr={pr} disabledReason={fixDisabledReason} />}
        {conflicts && <ResolveConflictsDialog pr={pr} disabledReason={fixDisabledReason} />}
        {pr.state === "merged" && pr.projectId && <DeployDialog pr={pr} />}
      </section>
      {fixJobActive && pr.state === "open" && (
        <p className="text-xs text-muted-foreground">
          The agent is working on this pull request. This page updates when it pushes new commits.
        </p>
      )}

      <AgentReviewCard pr={pr} />

      {pr.fixJobIds.length > 0 && (
        <section className="rounded-lg border p-4 space-y-2">
          <h2 className="text-sm font-semibold">Fix runs</h2>
          <ul className="space-y-1">
            {[...pr.fixJobIds].reverse().map((jobId) => (
              <FixJobRow key={jobId} jobId={jobId} />
            ))}
          </ul>
        </section>
      )}

      <DeploymentsList pr={pr} />

      <PullRequestFiles pr={pr} page={page} onPageChange={setPage} />
    </div>
  );
}

export default PullRequestReviewPage;
